if (!customElements.get('collector-testimonials')) {
  class CollectorTestimonials extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.visibleCards = 1;
      this.maxIndex = 0;
      this.scrollFrame = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleDotKeydown = this.handleDotKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-testimonial-slider]');
      this.track = this.querySelector('[data-testimonial-track]');
      this.cards = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
      this.pagination = this.querySelector('[data-testimonial-pagination]');
      this.dots = Array.from(this.querySelectorAll('[data-testimonial-dot]'));

      if (!this.viewport || !this.track || !this.cards.length) return;

      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.handleDotClick);
        dot.addEventListener('keydown', this.handleDotKeydown);
      });
      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
        this.cards.forEach((card) => this.resizeObserver.observe(card));
      } else {
        window.addEventListener('resize', this.handleResize);
      }

      requestAnimationFrame(() => this.refresh());
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.dots?.forEach((dot) => {
        dot.removeEventListener('click', this.handleDotClick);
        dot.removeEventListener('keydown', this.handleDotKeydown);
      });
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.handleResize);

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    }

    getGap() {
      const styles = window.getComputedStyle(this.track);
      return Number.parseFloat(styles.columnGap || styles.gap) || 0;
    }

    getVisibleCardCount() {
      const firstCard = this.cards[0];
      if (!firstCard) return 1;

      const step = firstCard.getBoundingClientRect().width + this.getGap();
      if (step <= 0) return 1;

      return Math.max(1, Math.floor((this.viewport.clientWidth + this.getGap()) / step));
    }

    refresh() {
      this.visibleCards = this.getVisibleCardCount();
      this.maxIndex = Math.max(0, this.cards.length - this.visibleCards);
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);

      this.dots.forEach((dot, index) => {
        dot.hidden = index > this.maxIndex;
      });

      if (this.pagination) {
        this.pagination.hidden = this.maxIndex === 0;
      }

      this.scrollToIndex(this.currentIndex, 'auto');
      this.updatePagination();
    }

    scrollToIndex(index, behavior = 'smooth') {
      const nextIndex = Math.max(0, Math.min(Number(index) || 0, this.maxIndex));
      const targetCard = this.cards[nextIndex];
      if (!targetCard) return;

      this.currentIndex = nextIndex;
      this.viewport.scrollTo({
        left: targetCard.offsetLeft - this.track.offsetLeft,
        behavior: this.motionQuery.matches ? 'auto' : behavior,
      });
      this.updatePagination();
    }

    updatePagination() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('collector-testimonials__dot--active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    updateCurrentIndexFromScroll() {
      const currentScroll = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (let index = 0; index <= this.maxIndex; index += 1) {
        const targetPosition = this.cards[index].offsetLeft - this.track.offsetLeft;
        const distance = Math.abs(targetPosition - currentScroll);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
        this.updatePagination();
      }
    }

    handleScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateCurrentIndexFromScroll());
    }

    handleResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.refresh());
    }

    handleDotClick(event) {
      this.scrollToIndex(event.currentTarget.dataset.slideIndex);
    }

    handleDotKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      let nextIndex = this.currentIndex;

      if (event.key === 'ArrowLeft') nextIndex -= 1;
      if (event.key === 'ArrowRight') nextIndex += 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.maxIndex;

      nextIndex = Math.max(0, Math.min(nextIndex, this.maxIndex));
      this.scrollToIndex(nextIndex);
      this.dots[nextIndex]?.focus();
    }

    handleBlockSelect(event) {
      const selectedCard = event.target.closest?.('[data-testimonial-slide]');
      if (!selectedCard) return;

      const selectedIndex = this.cards.indexOf(selectedCard);
      if (selectedIndex < 0) return;

      this.scrollToIndex(Math.min(selectedIndex, this.maxIndex), 'auto');
    }
  }

  customElements.define('collector-testimonials', CollectorTestimonials);
}
